import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import BrandCornerTag from '../components/BrandCornerTag';

const AI_AUTOMATION_URL = process.env.REACT_APP_AI_AUTOMATION_URL || 'http://localhost:8100/aibot/v1';

const InterviewBookingPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
  const loadHandledRef = useRef(false);
  const fetchedTokenRef = useRef(null);

  // Fetch HTML content from backend
  useEffect(() => {
    const fetchBookingForm = async () => {
      try {
        const response = await fetch(`${AI_AUTOMATION_URL}/interviews/book/${token}`, {
          method: 'GET',
          headers: { 'Accept': 'text/html' },
        });

        if (!response.ok) {
          let errorMessage = 'Failed to load booking form';
          try {
            const errorData = await response.text();
            try {
              const jsonError = JSON.parse(errorData);
              errorMessage = jsonError.detail || jsonError.message || errorMessage;
            } catch {
              if (response.status === 404) {
                errorMessage = 'Invalid or expired booking link';
              } else if (response.status === 500) {
                errorMessage = 'Server error. Please try again later.';
              } else {
                errorMessage = `Error ${response.status}: ${errorData.substring(0, 100)}`;
              }
            }
          } catch (err) {
            if (response.status === 404) {
              errorMessage = 'Invalid or expired booking link';
            } else if (response.status === 500) {
              errorMessage = 'Server error. Please try again later.';
            }
          }
          console.error('Booking form fetch error:', response.status, errorMessage);
          setError(errorMessage);
          setLoading(false);
          return;
        }

        const html = await response.text();
        setHtmlContent(html);
      } catch (err) {
        console.error('Error fetching booking form:', err);
        let errorMessage = 'Failed to load booking form. Please try again.';
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          errorMessage = 'Cannot connect to server. Please check your connection and ensure the backend is running.';
        } else {
          errorMessage = `Error: ${err.message}`;
        }
        setError(errorMessage);
        setLoading(false);
      }
    };

    if (token && fetchedTokenRef.current !== token) {
      fetchedTokenRef.current = token;
      fetchBookingForm();
    }
  }, [token]);

  // Load HTML into iframe once both HTML is fetched AND iframe is mounted
  useEffect(() => {
    if (!htmlContent || !iframeRef.current) {
      return;
    }

    loadHandledRef.current = false;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    const handleIframeLoad = () => {
      if (loadHandledRef.current) return;
      loadHandledRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setLoading(false);
    };

    const handleIframeError = () => {
      if (loadHandledRef.current) return;
      loadHandledRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setError('Failed to load booking form content. Please try again.');
      setLoading(false);
    };

    // Timeout fallback (10 seconds)
    timeoutRef.current = setTimeout(() => {
      if (loadHandledRef.current) return;
      loadHandledRef.current = true;
      setLoading(false);
    }, 10000);

    iframeRef.current.addEventListener('load', handleIframeLoad);
    iframeRef.current.addEventListener('error', handleIframeError);

    iframeRef.current.src = url;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (iframeRef.current && iframeRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(iframeRef.current.src);
      }
    };
  }, [htmlContent]);

  return (
    <div style={{ width: '100%', minHeight: '100vh', margin: 0, padding: 0, position: 'relative' }}>
      {/* Persistent app branding (over the loading overlay and the booking iframe) */}
      <BrandCornerTag />

      {/* Loading overlay */}
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'white',
          zIndex: 10,
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto',
            }}></div>
            <p style={{ marginTop: '20px' }}>Loading booking form...</p>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          padding: '20px',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'white',
          zIndex: 10,
        }}>
          <div style={{ textAlign: 'center', maxWidth: '500px' }}>
            <h1 style={{ color: '#d32f2f', marginBottom: '20px' }}>Error</h1>
            <p style={{ color: '#666', marginBottom: '20px' }}>{error}</p>
            <p style={{ color: '#999', fontSize: '14px' }}>
              Please contact the interviewer for assistance.
            </p>
          </div>
        </div>
      )}

      {/* Iframe - always rendered so ref is available */}
      <iframe
        ref={iframeRef}
        title="Interview Booking Form"
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          margin: 0,
          padding: 0,
          display: (loading || error) ? 'none' : 'block',
        }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default InterviewBookingPage;
